import React, { useEffect, useState } from 'react';
import { Email, Link as LinkIcon, Phone, Telegram } from '@mui/icons-material';
import { Chip, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { checkEmpty } from '@/utils/commonFunctions';
import { GLOBAL_CONSTANTS } from '@/utils/globalConstants';
import { StyledButton, StyledConnectContainer, StyledContainer, StyledTextField } from './styles';

export default function ContactMe({ sendMessageRequest, messageSentResponse, messageSentError }) {
  const [formFields, setFormFields] = useState({ fullname: '', email: '', message: '' });
  const [formErrors, setFormErrors] = useState({ fullname: '', email: '', message: '' });
  const [fullnameValid, setFullnameValid] = useState(false);
  const [emailValid, setEmailValid] = useState(false);
  const [messageValid, setMesageValid] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [sending, setIsSending] = useState(false);

  useEffect(() => {
    if (!checkEmpty(messageSentResponse) && messageSentResponse?.timestamp) {
      setFormFields({ fullname: '', email: '', message: '' });
      setFormErrors({ fullname: '', email: '', message: '' });
      setIsFormValid(false);
      setIsSending(false);
    }
  }, [messageSentResponse]);

  useEffect(() => {
    if (!checkEmpty(messageSentError)) {
      setIsSending(false);
    }
  }, [messageSentError]);

  const getTypeName = (type) => {
    if (type === 'fullname') {
      return 'Fullname';
    }
    if (type === 'email') {
      return 'Email';
    }
    if (type === 'message') {
      return 'Message';
    }
    return '';
  };
  const geValidationMessage = (formValidationErrors, fieldIsValid, type, value) => {
    if (!fieldIsValid && !value) {
      formValidationErrors[type] = `${getTypeName(type)} is required`;
    } else if (!fieldIsValid) {
      formValidationErrors[type] = type === 'message' ? 'Please write meaningful message' : `${getTypeName(type)} is not valid`;
    } else {
      formValidationErrors[type] = '';
    }
    return formValidationErrors;
  };

  const validateField = (type, value) => {
    const {
      REGEX: { EMAIL, FULLNAME },
    } = GLOBAL_CONSTANTS;
    const formValidationErrors = formErrors;
    let fieldIsValid = true;
    if (type === 'fullname') {
      fieldIsValid = !!value && value?.length <= 50 && FULLNAME.test(value);
      Object.assign(formValidationErrors, { ...geValidationMessage(formValidationErrors, fieldIsValid, type, value) });
      setFullnameValid(fieldIsValid);
    } else if (type === 'email') {
      fieldIsValid = !!value && EMAIL.test(value);
      Object.assign(formValidationErrors, { ...geValidationMessage(formValidationErrors, fieldIsValid, type, value) });
      setEmailValid(fieldIsValid);
    } else {
      fieldIsValid = !!value && value?.length > 10 && value?.length <= 200;
      Object.assign(formValidationErrors, { ...geValidationMessage(formValidationErrors, fieldIsValid, type, value) });
      setMesageValid(fieldIsValid);
    }

    setFormErrors(formValidationErrors);
    setIsFormValid(fullnameValid && emailValid && messageValid);
  };

  const handleUserInput = ({ target: { name, value } }) => {
    setFormFields((prevState) => ({ ...prevState, [name]: value }));
    validateField(name, value);
  };

  const sendMessage = (event) => {
    event.preventDefault();
    const mailFormData = { fullname, email, message };
    setIsSending(true);
    sendMessageRequest(mailFormData);
  };

  const { fullname, email, message } = formFields;
  const {
    externalLinks: { linktree, phone, emailTo },
    personalData: { mobile, email: personalEmail },
  } = GLOBAL_CONSTANTS;
  return (
    <StyledContainer id="contact" className="px-4 pt-10 mx-auto">
      <Box
        component="form"
        name="contact"
        className="lg:w-1/2 md:w-1/2 flex flex-col justify-center md:m-auto w-full md:py-8 mt-8 md:mt-0"
        noValidate
        autoComplete="off"
        onSubmit={sendMessage}
      >
        <h2 className="text-white sm:text-4xl text-3xl mb-1 font-medium title-font">Contact Me</h2>
        <p className="leading-relaxed mb-5">Want to know more about me?</p>
        <StyledTextField
          id="fullname"
          value={fullname}
          name="fullname"
          label="Enter Fullname"
          onChange={handleUserInput}
          error={!fullnameValid && !!formErrors.fullname}
          helperText={formErrors.fullname}
          required
          margin="normal"
          inputProps={{
            maxLength: 50,
          }}
          onBlur={handleUserInput}
        />
        <StyledTextField
          id="email"
          value={email}
          name="email"
          label="Enter Email"
          onChange={handleUserInput}
          error={!emailValid && !!formErrors.email}
          helperText={formErrors.email}
          required
          margin="normal"
          inputProps={{
            maxLength: 50,
          }}
          onBlur={handleUserInput}
        />
        <StyledTextField
          id="message"
          value={message}
          name="message"
          label="Enter Message"
          onChange={handleUserInput}
          error={!messageValid && !!formErrors.message}
          helperText={formErrors.message}
          required
          multiline
          margin="normal"
          rows={5}
          inputProps={{
            maxLength: 200,
          }}
          onBlur={handleUserInput}
        />
        <StyledButton
          variant="outlined"
          endIcon={<Telegram />}
          type="submit"
          focusRipple
          size="large"
          color="success"
          disabled={!isFormValid}
          disableRipple={!isFormValid}
          loading={sending}
          loadingIndicator="Sending..."
        >
          Send
        </StyledButton>
      </Box>
      <StyledConnectContainer className="mt-5">
        <div className="mx-1 my-2">
          <Tooltip title="Call Me" arrow>
            <Chip icon={<Phone />} label={mobile} color="secondary" component="a" href={phone} clickable />
          </Tooltip>
        </div>
        <div className="mx-1 my-2">
          <Tooltip title="Mail Me" arrow>
            <Chip icon={<Email />} label={personalEmail} color="secondary" component="a" href={emailTo} target="_blank" clickable />
          </Tooltip>
        </div>
        <div className="mx-1 my-2">
          <Chip icon={<LinkIcon />} label="Connect Me" color="secondary" component="a" href={linktree} target="_blank" clickable />
        </div>
      </StyledConnectContainer>
    </StyledContainer>
  );
}

ContactMe.propTypes = {
  sendMessageRequest: PropTypes.func.isRequired,
  messageSentResponse: PropTypes.oneOfType([PropTypes.object]).isRequired,
  messageSentError: PropTypes.oneOfType([PropTypes.object]).isRequired,
};
